import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Megaphone, Plus, Trash2, Edit, Eye, EyeOff,
  Loader2, Image, X, Upload, AlertTriangle,
  Info, Zap, Gift, RefreshCw, Clock
} from 'lucide-react';
import { API } from '../config/api';

const TYPE_OPTIONS = [
  { value: 'info', label: 'Info', icon: Info },
  { value: 'beta_update', label: 'Beta Update', icon: Zap },
  { value: 'promo', label: 'Promotion', icon: Gift },
  { value: 'warning', label: 'Warning / Maintenance', icon: AlertTriangle },
];

const DISPLAY_OPTIONS = [
  { value: 'modal', label: 'Popup Modal' },
  { value: 'banner_top', label: 'Top Banner' },
  { value: 'banner_bottom', label: 'Bottom Banner' },
];

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Everyone' },
  { value: 'logged_in', label: 'Logged-In Users' },
  { value: 'logged_out', label: 'Visitors (Logged Out)' },
  { value: 'listener', label: 'Listeners' },
  { value: 'artist', label: 'Artists' },
  { value: 'podcaster', label: 'Podcasters' },
  { value: 'streamer', label: 'Streamers' },
  { value: 'studio_engineer', label: 'Studio Engineers' },
  { value: 'videographer', label: 'Videographers' },
  { value: 'graphic_designer', label: 'Graphic Designers' },
];

const PRIORITY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High Priority' },
];

export default function AdminAnnouncementsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const emptyForm = {
    title: '', message: '', image_url: '',
    cta_text: '', cta_link: '',
    announcement_type: 'info', display_type: 'modal',
    audience: 'all', priority: 'normal',
    force_reshow: false, active: true,
    start_at: '', end_at: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (user && user.role !== 'owner' && user.role !== 'admin') {
      navigate('/');
    } else if (user) {
      fetchAnnouncements();
    }
  }, [user]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data.announcements || []);
      }
    } catch { toast.error('Failed to fetch announcements'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSubmitting(true);
    try {
      const url = editing
        ? `${API}/admin/announcements/${editing}`
        : `${API}/admin/announcements`;
      const method = editing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        toast.success(editing ? 'Announcement updated!' : 'Announcement created!');
        setShowForm(false);
        setEditing(null);
        setForm(emptyForm);
        fetchAnnouncements();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed to save announcement'); }
    finally { setSubmitting(false); }
  };

  const handleImageUpload = async (e, annId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/admin/announcements/${annId}/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Image uploaded');
        // Update form if we're still editing
        if (editing === annId) {
          setForm(prev => ({ ...prev, image_url: data.image_url }));
        }
        fetchAnnouncements();
      } else {
        toast.error('Image upload failed');
      }
    } catch { toast.error('Image upload failed'); }
    finally { setUploadingImage(false); }
  };

  const toggleActive = async (ann) => {
    try {
      await fetch(`${API}/admin/announcements/${ann.announcement_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active: !ann.active }),
      });
      toast.success(ann.active ? 'Announcement deactivated' : 'Announcement activated');
      fetchAnnouncements();
    } catch { toast.error('Failed to toggle'); }
  };

  const deleteAnnouncement = async (annId) => {
    if (!window.confirm('Delete this announcement permanently?')) return;
    try {
      const res = await fetch(`${API}/admin/announcements/${annId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      }
    } catch { toast.error('Failed to delete'); }
  };

  const startEdit = (ann) => {
    setForm({
      title: ann.title || '',
      message: ann.message || '',
      image_url: ann.image_url || '',
      cta_text: ann.cta_text || '',
      cta_link: ann.cta_link || '',
      announcement_type: ann.announcement_type || 'info',
      display_type: ann.display_type || 'modal',
      audience: ann.audience || 'all',
      priority: ann.priority || 'normal',
      force_reshow: ann.force_reshow || false,
      active: ann.active !== false,
      start_at: ann.start_at || '',
      end_at: ann.end_at || '',
    });
    setEditing(ann.announcement_id);
    setShowForm(true);
  };

  const typeIcon = (type) => {
    const opt = TYPE_OPTIONS.find(o => o.value === type);
    if (opt) { const I = opt.icon; return <I className="w-4 h-4" />; }
    return <Info className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-announcements-page">
      <div className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">Global Announcements</h1>
                <p className="text-sm text-muted-foreground">Manage platform-wide announcements</p>
              </div>
            </div>
            <Button
              onClick={() => { setForm(emptyForm); setEditing(null); setShowForm(true); }}
              className="bg-amber-500 hover:bg-amber-500/80 text-black font-semibold"
              data-testid="new-announcement-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Announcement
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Create / Edit Form */}
        {showForm && (
          <Card className="bg-card border-amber-500/20 p-6" data-testid="announcement-form">
            <h3 className="font-heading font-bold text-lg mb-4">
              {editing ? 'Edit Announcement' : 'Create Announcement'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Title *</label>
                <Input
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Announcement title"
                  data-testid="ann-title-input"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Message *</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  placeholder="Announcement message..."
                  className="w-full min-h-[100px] rounded-lg bg-background border border-border p-3 text-sm text-foreground resize-y focus:outline-none focus:border-amber-500/40"
                  data-testid="ann-message-input"
                />
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Type</label>
                  <Select value={form.announcement_type} onValueChange={v => setForm(p => ({ ...p, announcement_type: v }))}>
                    <SelectTrigger data-testid="ann-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Display</label>
                  <Select value={form.display_type} onValueChange={v => setForm(p => ({ ...p, display_type: v }))}>
                    <SelectTrigger data-testid="ann-display-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DISPLAY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Audience</label>
                  <Select value={form.audience} onValueChange={v => setForm(p => ({ ...p, audience: v }))}>
                    <SelectTrigger data-testid="ann-audience-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AUDIENCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Priority</label>
                  <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                    <SelectTrigger data-testid="ann-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.force_reshow}
                      onChange={e => setForm(p => ({ ...p, force_reshow: e.target.checked }))}
                      className="rounded"
                      data-testid="ann-force-reshow"
                    />
                    Force re-show (clears dismissals)
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={e => setForm(p => ({ ...p, active: e.target.checked }))}
                      className="rounded"
                      data-testid="ann-active-toggle"
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">CTA Button Text</label>
                  <Input
                    value={form.cta_text}
                    onChange={e => setForm(p => ({ ...p, cta_text: e.target.value }))}
                    placeholder="e.g. Learn More"
                    data-testid="ann-cta-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">CTA Button Link</label>
                  <Input
                    value={form.cta_link}
                    onChange={e => setForm(p => ({ ...p, cta_link: e.target.value }))}
                    placeholder="https://..."
                    data-testid="ann-cta-link"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Start Date/Time</label>
                  <Input
                    type="datetime-local"
                    value={form.start_at ? form.start_at.slice(0, 16) : ''}
                    onChange={e => setForm(p => ({ ...p, start_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                    data-testid="ann-start-at"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">End Date/Time</label>
                  <Input
                    type="datetime-local"
                    value={form.end_at ? form.end_at.slice(0, 16) : ''}
                    onChange={e => setForm(p => ({ ...p, end_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                    data-testid="ann-end-at"
                  />
                </div>
              </div>

              {/* Image Upload (only after creation) */}
              {editing && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1">Image</label>
                  <div className="flex items-center gap-3">
                    {form.image_url && (
                      <img src={form.image_url.startsWith('/') ? `${window.location.origin}${form.image_url}` : form.image_url} alt="" className="w-20 h-12 rounded object-cover" />
                    )}
                    <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={e => handleImageUpload(e, editing)} />
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage} data-testid="ann-upload-image-btn">
                      {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      Upload Image
                    </Button>
                    <span className="text-[10px] text-muted-foreground">or</span>
                    <Input
                      value={form.image_url}
                      onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
                      placeholder="Paste image URL"
                      className="flex-1 text-xs"
                      data-testid="ann-image-url-input"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-500 hover:bg-amber-500/80 text-black font-semibold" data-testid="ann-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {editing ? 'Update' : 'Create'} Announcement
                </Button>
                <Button variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
              </div>
            </div>
          </Card>
        )}

        {/* Announcements List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : announcements.length === 0 ? (
          <Card className="bg-card border-border p-8 text-center" data-testid="no-announcements">
            <Megaphone className="w-10 h-10 mx-auto mb-3 text-muted-foreground/80" />
            <p className="text-sm text-muted-foreground">No announcements yet</p>
            <p className="text-[10px] text-muted-foreground/80 mt-1">Create one to reach all your users</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {announcements.map(ann => (
              <Card
                key={ann.announcement_id}
                className={`bg-card border-border p-4 ${!ann.active ? 'opacity-50' : ''}`}
                data-testid={`ann-card-${ann.announcement_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Image thumbnail */}
                  {ann.image_url && (
                    <img
                      src={ann.image_url.startsWith('/') ? `${window.location.origin}${ann.image_url}` : ann.image_url}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {typeIcon(ann.announcement_type)}
                      <h4 className="font-bold text-sm">{ann.title}</h4>
                      <Badge className={ann.active ? 'bg-green-500/20 text-green-400 text-[10px]' : 'bg-muted/40 text-muted-foreground text-[10px]'}>
                        {ann.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">{ann.display_type.replace('_', ' ')}</Badge>
                      <Badge variant="outline" className="text-[10px]">{ann.audience}</Badge>
                      {ann.priority === 'high' && <Badge className="bg-red-500/20 text-red-400 text-[10px]">High Priority</Badge>}
                      {ann.force_reshow && <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Force Re-show</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-1">{ann.message}</p>
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(ann.created_at).toLocaleDateString()}</span>
                      {ann.dismissal_count > 0 && <span>{ann.dismissal_count} dismissals</span>}
                      {ann.end_at && <span>Expires: {new Date(ann.end_at).toLocaleDateString()}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleActive(ann)} data-testid={`toggle-ann-${ann.announcement_id}`}>
                      {ann.active ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-green-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => startEdit(ann)} data-testid={`edit-ann-${ann.announcement_id}`}>
                      <Edit className="w-4 h-4 text-blue-400" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => deleteAnnouncement(ann.announcement_id)} data-testid={`delete-ann-${ann.announcement_id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
